import { useEffect, useState, useRef, useCallback } from 'react'
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Keyboard, Platform, KeyboardAvoidingView, ScrollView, RefreshControl, ActivityIndicator } from 'react-native'
import { useCommunicateStore } from '../../stores/communicate'
import { useTranslation } from 'react-i18next'
import { colorPalette, globalStyles } from '../../static/styles/global'
import Card from '../../components/shared/card/Card'
import HomeDetailsHeader from '../../components/home/HomeDetailsHeader'
import FontAwesome from 'react-native-vector-icons/FontAwesome'
import { formatMessageDate } from '../../helpers/globalHelper'

const CommunicateWithEmployee = ({ navigation, route }: any): JSX.Element => {

    const { params } = route
    const { mostRecentUserOnBoardings: userId, refresh, pUserKey, pObKey } = params
    const { getConversation, conversation, createEmployeeMessage, patchEmployeeMessage, clearConversation } = useCommunicateStore((store) => ({
        getConversation: store.getConversation,
        conversation: store.conversation,
        createEmployeeMessage: store.createEmployeeMessage,
        patchEmployeeMessage: store.patchEmployeeMessage,
        clearConversation: store.clearConversation
    }))
    const { t } = useTranslation()
    const [message, setMessage] = useState('')
    const scrollViewRef = useRef<any>(null)
    const [refreshing, setRefreshing] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [inputHeight, setInputHeight] = useState(44)

    const onRefresh = useCallback(async () => {
        setRefreshing(true)
        if (userId && pUserKey && pObKey) {
            try {
                await getConversation(userId, pUserKey, pObKey)
            } catch (err) {
                console.error("Failed to refresh conversation:", err)
            }
        }
        setRefreshing(false)
    }, [userId, pUserKey, pObKey])

    useEffect(() => {
        setIsLoading(true)
        if (userId && pUserKey && pObKey) {
            getConversation(userId, pUserKey, pObKey).finally(() => {
                setIsLoading(false)
            })
        } else {
            setIsLoading(false)
        }
        return () => {
            clearConversation()
        }
    }, [userId, pUserKey, pObKey])

    useEffect(() => {
        if (conversation?.data) {
            const timer = setTimeout(() => {
                scrollViewRef.current?.scrollToEnd({ animated: true })
            }, 100)
            return () => clearTimeout(timer)
        }
    }, [conversation])



    const handleSendMessage = async (): Promise<void> => {
        if (message.trim() === '') return
        Keyboard.dismiss()

        try {
            const recentMessage = conversation?.data && conversation.data.length > 0
                ? conversation.data[0]
                : null

            if (recentMessage && recentMessage.receiverEssUserOnBoardingKey === userId && !recentMessage.response) {
                await patchEmployeeMessage(userId, recentMessage.essEmployeeMessageKey, {
                    response: message.trim(),
                    isNew: false,
                    isDeleted: false
                })
            } else {
                let msgSubject = "General Message"
                if (conversation?.data && conversation.data.length > 0) {
                    for (let i = 0; i < conversation.data.length; i++) {
                        if (conversation.data[i].subject) {
                            msgSubject = conversation.data[i].subject
                            break
                        }
                    }
                }

                await createEmployeeMessage(userId, {
                    receiverEssUserKey: pUserKey,
                    receiverEssUserOnBoardingKey: pObKey,
                    subject: msgSubject,
                    message: message.trim()
                })
            }

            if (userId && pUserKey && pObKey) {
                await getConversation(userId, pUserKey, pObKey)
            }
            setMessage('')
        } catch (error) {
            console.error("Failed to send message:", error)
        }
    }
    const getPartnerName = (): string => {
        if (conversation?.data && conversation.data.length > 0) {
            const firstMsg = conversation.data[0]
            if (firstMsg.senderEssUserOnBoardingKey === userId) {
                return firstMsg.receiverName || t('Messages')
            } else {
                return firstMsg.senderName || t('Messages')
            }
        }
        return t('Messages')
    }

    const renderMessageBubble = (
        text: string,
        senderName: string,
        date: string | null,
        isMe: boolean,
        key: string
    ) => {
        if (!text) return null

        // Clean up escaped newlines (e.g. \\n)
        const formattedText = typeof text === 'string' ? text.replace(/\\n/g, '\n') : text

        return (
            <View key={key} style={styles.rowContainer}>
                <View
                    style={[
                        globalStyles.hDirection,
                        globalStyles.hBetweenItems,
                        globalStyles.mb1,
                        isMe ? styles.leftIndent : styles.rightIndent,
                        styles.labelGap
                    ]}
                >
                    <Text style={styles.labelText}>
                        {senderName}
                    </Text>
                    {date && (
                        <Text style={styles.timeText}>
                            {formatMessageDate(date)}
                        </Text>
                    )}
                </View>
                <View
                    style={[
                        styles.bubbleBox,
                        isMe ? styles.bubbleRightBox : styles.bubbleLeftBox,
                        isMe ? styles.leftIndent : styles.rightIndent
                    ]}
                >
                    <Text style={[globalStyles.text, isMe ? styles.bubbleRightText : styles.bubbleLeftText]}>
                        {formattedText}
                    </Text>
                </View>
            </View>
        )
    }

    return (
        <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={{ flex: 1, backgroundColor: colorPalette.lighter || '#f8f9fa' }}
        >
            <View style={[globalStyles.primaryBgColor, globalStyles.containerBand]}>
                <View style={[globalStyles.horizontalPadGap]}>
                    <HomeDetailsHeader
                        navLabel={t('Messages')}
                        title={getPartnerName()}
                        navScreen={"CommunicateWithAdmin"}
                    // description={t('hiringmanagerdescription')}
                    />
                </View>
            </View>
            <View style={{
                flex: 1, marginTop: -44,

            }}>
                <Card style={{ flex: 1, marginBottom: 0 }}>
                    <ScrollView
                        ref={scrollViewRef}
                        style={[
                            globalStyles.mb1,
                            { marginHorizontal: -20 }
                        ]}
                        // onScroll={handleMsgBoxScroll}
                        scrollEventThrottle={1000}
                        nestedScrollEnabled={true}
                        onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: true })}
                        refreshControl={
                            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                        }
                    >
                        <View style={{ paddingHorizontal: 20 }}>
                            {isLoading ? (
                                <ActivityIndicator size="large" color={colorPalette.secondary} style={{ marginTop: 20 }} />
                            ) : (
                                conversation?.data && conversation.data.length > 0 &&
                                [...conversation.data].reverse().map((msgData: any) => {
                                    const isOriginalMsgMe = msgData.senderEssUserOnBoardingKey === userId
                                    const senderNameOfOriginal = isOriginalMsgMe ? t('you') : (msgData.senderName || '')

                                    const originalBubble = renderMessageBubble(
                                        msgData.message,
                                        senderNameOfOriginal,
                                        msgData.messageSentDate,
                                        isOriginalMsgMe,
                                        `orig-${msgData.essEmployeeMessageKey}`
                                    )

                                    const responseBubble = msgData.response ? renderMessageBubble(
                                        msgData.response,
                                        isOriginalMsgMe ? (msgData.receiverName || '') : t('you'),
                                        msgData.messageResponseDate,
                                        !isOriginalMsgMe,
                                        `resp-${msgData.essEmployeeMessageKey}`
                                    ) : null

                                    return (
                                        <View key={`group-${msgData.essEmployeeMessageKey}`} style={{ paddingHorizontal: 16 }}>
                                            {originalBubble}
                                            {responseBubble}
                                        </View>
                                    )
                                })
                            )}
                        </View>
                    </ScrollView>
                    <View style={[styles.footer, styles.noBorder]}>
                        <View
                            style={[globalStyles.flexOne, styles.messageInputWrapper]}
                        >
                            <TextInput
                                multiline
                                style={[globalStyles.input, styles.messageInput,
                                { height: Math.min(100, Math.max(44, inputHeight)) }]}
                                placeholder={t('message')}
                                placeholderTextColor={colorPalette.lightGray}
                                value={message}
                                onChangeText={(text) => setMessage(text)}
                                onContentSizeChange={(event) => {
                                    setInputHeight(event.nativeEvent.contentSize.height)
                                }}
                            />
                            <TouchableOpacity
                                style={[styles.messageSend]}
                                onPress={handleSendMessage}
                                disabled={message.trim() === ''}
                            >
                                <FontAwesome
                                    name="send"
                                    size={26}
                                    color={colorPalette.secondary}
                                    style={[
                                        message.trim() === '' && { opacity: 0.3 }
                                    ]}
                                />
                            </TouchableOpacity>
                        </View>
                    </View>
                </Card >
            </View>
        </KeyboardAvoidingView>
    )
}

const styles = StyleSheet.create({
    labelSubject: {
        borderLeftWidth: 2,
        borderLeftColor: colorPalette.secondary,
        paddingLeft: 10
    },
    inputSubject: {
        borderWidth: 0,
        borderRadius: 0,
        borderBottomColor: colorPalette.darkLight,
        borderBottomWidth: 1,
        paddingHorizontal: 5
    },
    body: {
        paddingTop: 20,
        paddingBottom: 30,
        paddingHorizontal: 20
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        width: '100%',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderTopWidth: 1,
        borderColor: colorPalette.darkLight
    },
    noBorder: {
        borderColor: 'transparent'
    },
    textArea: {
        minHeight: 100,
        maxHeight: 300,
        paddingVertical: 20,
        paddingHorizontal: 10
    },
    msgWrapper: {
        marginHorizontal: -10,
        marginVertical: -10
    },
    msgRequest: {
        marginLeft: 70
    },
    msgResponse: {
        marginRight: 70
    },
    msgBox: {
        backgroundColor: 'rgba(118, 216, 212, .2)',
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 16,
        marginBottom: 6
    },
    msgBoxResponse: {
        backgroundColor: 'rgba(200, 200, 200, .2)'
    },
    rowContainer: {
        width: '100%',
        marginVertical: 6
    },
    rightIndent: {
        marginRight: 60
    },
    leftIndent: {
        marginLeft: 60
    },
    labelText: {
        fontSize: 12,
        color: colorPalette.gray
    },
    timeText: {
        fontSize: 12,
        color: colorPalette.gray
    },
    bubbleBox: {
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 16
    },
    bubbleLeftBox: {
        backgroundColor: colorPalette.light
    },
    bubbleRightBox: {
        backgroundColor: colorPalette.lightBlue
    },
    bubbleLeftText: {
        color: colorPalette.black,
        fontSize: 14,
        lineHeight: 20
    },
    bubbleRightText: {
        color: colorPalette.black,
        fontSize: 14,
        lineHeight: 20
    },
    labelGap: {
        paddingHorizontal: 2
    },
    messageInput: {
        textAlignVertical: 'top',
        borderColor: colorPalette.lightGray,
        borderRadius: 50,
        minHeight: 44,
        paddingHorizontal: 20,
        paddingRight: 60,
        paddingTop: 11
    },
    messageInputWrapper: {
        position: 'relative'
    },
    messageSend: {
        position: 'absolute',
        right: 18,
        top: '50%',
        marginTop: -11
    },
    employeeHeaderContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
        marginBottom: 10
    },
    statusText: {
        fontSize: 14,
        fontWeight: 'bold',
        textDecorationLine: 'underline'
    }
})

export default CommunicateWithEmployee
